/**
 * DERAR2Geocoder - Geocodificador L4 usando DERA desde Worker R2
 *
 * Busca infraestructuras en los 11,282 registros DERA de Andalucía
 * mediante búsqueda fuzzy por nombre y filtrado por tipología.
 *
 * Nivel en cascada: L4
 * Precisión típica: ±5-15m (coordenadas oficiales DERA)
 *
 * @module services/geocoding/specialized
 * @version 1.0.0
 */

import uFuzzy from '@leeoniya/ufuzzy';
import type { DERAR2Service } from '../../dera/DERAR2Service';
import { getDERAR2Service } from '../../dera/DERAR2Service';
import type {
  DERAR2Feature,
  DERAR2SearchOptions,
  DERAR2Result,
  DERATipologia,
} from '../../../types/deraR2';
import {
  X_RANGE_ANDALUCIA,
  Y_RANGE_ANDALUCIA,
  DERA_TIPOLOGIAS,
} from '../../../types/deraR2';
import type { GeocodingResult } from '../../../types/infrastructure';

// =============================================================================
// CONSTANTES
// =============================================================================

/** Configuración uFuzzy optimizada para nombres españoles */
const UFUZZY_OPTIONS: uFuzzy.Options = {
  intraMode: 1, // Permitir caracteres dentro de palabras
  intraIns: 1, // Inserciones intra-palabra
  interIns: 6, // Inserciones entre palabras (más tolerante)
  intraSub: 1, // Sustituciones intra-palabra
  intraTrn: 1, // Transposiciones intra-palabra
  intraDel: 1, // Eliminaciones intra-palabra
};

/** Precisión estimada DERA (metros) */
const DERA_PRECISION_M = 10;

// =============================================================================
// SINGLETON
// =============================================================================

let geocoderInstance: DERAR2Geocoder | null = null;

export function getDERAR2Geocoder(): DERAR2Geocoder {
  if (!geocoderInstance) {
    geocoderInstance = new DERAR2Geocoder();
  }
  return geocoderInstance;
}

/** Reset singleton (útil para tests) */
export function resetDERAR2Geocoder(): void {
  geocoderInstance = null;
}

// =============================================================================
// CLASE PRINCIPAL
// =============================================================================

export class DERAR2Geocoder {
  private readonly service: DERAR2Service;
  private readonly fuzzy: uFuzzy;

  constructor(service?: DERAR2Service) {
    this.service = service || getDERAR2Service();
    this.fuzzy = new uFuzzy(UFUZZY_OPTIONS);
  }

  /**
   * Geocodifica una infraestructura usando DERA R2
   * Retorna el mejor resultado o null si no hay coincidencias
   */
  async geocode(options: DERAR2SearchOptions): Promise<GeocodingResult | null> {
    const results = await this.search(options);
    if (results.length === 0) return null;
    return this.toGeocodingResult(results[0], options.name);
  }

  /**
   * Busca infraestructuras que coincidan con los criterios
   * Retorna array ordenado por confianza descendente
   */
  async search(options: DERAR2SearchOptions): Promise<DERAR2Result[]> {
    const { codINE, name, tipologia, subtipo, minConfidence = 60, maxResults = 5 } = options;

    // Cargar features del municipio
    const features = await this.service.loadMunicipio(codINE);
    if (features.length === 0) return [];

    // Filtrar por tipología/subtipo si especificado
    let candidates = features;
    if (tipologia) {
      candidates = candidates.filter((f) => f.tipologia === tipologia);
    }
    if (subtipo) {
      candidates = candidates.filter(
        (f) => f.subtipo.toUpperCase() === subtipo.toUpperCase()
      );
    }

    if (candidates.length === 0) return [];

    // Si no hay nombre, retornar candidatos filtrados (sin fuzzy)
    if (!name || name.trim() === '') {
      return candidates
        .filter((f) => this.isValidCoord(f))
        .slice(0, maxResults)
        .map((f) => this.toResult(f, 70)); // Confianza base sin fuzzy
    }

    // Búsqueda fuzzy por nombre
    return this.fuzzySearch(candidates, name, minConfidence, maxResults);
  }

  /**
   * Búsqueda fuzzy usando uFuzzy
   */
  private fuzzySearch(
    candidates: DERAR2Feature[],
    searchName: string,
    minConfidence: number,
    maxResults: number
  ): DERAR2Result[] {
    const haystack = candidates.map((f) => f.nombre);
    const searchResult = this.fuzzy.search(haystack, searchName);

    if (!searchResult || !searchResult[0] || searchResult[0].length === 0) {
      return [];
    }

    const [idxs, info, order] = searchResult;
    const results: DERAR2Result[] = [];
    const processOrder = order || idxs;

    for (let i = 0; i < Math.min(processOrder.length, maxResults * 2); i++) {
      const idx = processOrder[i];
      const featureIdx = idxs[idx];
      const feature = candidates[featureIdx];

      if (!this.isValidCoord(feature)) continue;

      // Calcular confianza basada en score fuzzy
      const fuzzyScore = this.calculateFuzzyScore(info, searchName, feature.nombre);
      const confidence = this.fuzzyScoreToConfidence(fuzzyScore);

      if (confidence >= minConfidence) {
        results.push(this.toResult(feature, confidence, fuzzyScore));
      }

      if (results.length >= maxResults) break;
    }

    return results;
  }

  /**
   * Calcula score normalizado 0-1 del fuzzy match
   */
  private calculateFuzzyScore(
    info: uFuzzy.Info | null,
    needle: string,
    haystack: string
  ): number {
    if (!info) return 0.5;

    const needleLower = needle.toLowerCase();
    const haystackLower = haystack.toLowerCase();

    // Match exacto
    if (haystackLower === needleLower) return 1.0;

    // Contiene exacto
    if (haystackLower.includes(needleLower)) return 0.95;

    // Basado en longitud de caracteres matcheados
    const matchRatio = needle.length / Math.max(haystack.length, needle.length);
    return Math.min(0.9, 0.5 + matchRatio * 0.4);
  }

  /**
   * Convierte score fuzzy (0-1) a confianza (0-100)
   */
  private fuzzyScoreToConfidence(score: number): number {
    if (score >= 0.95) return 95;
    if (score >= 0.85) return 88;
    if (score >= 0.7) return 78;
    if (score >= 0.55) return 68;
    if (score >= 0.4) return 58;
    return 50;
  }

  /**
   * Valida que las coordenadas estén en rango Andalucía
   */
  private isValidCoord(feature: DERAR2Feature): boolean {
    return (
      feature.x >= X_RANGE_ANDALUCIA.min &&
      feature.x <= X_RANGE_ANDALUCIA.max &&
      feature.y >= Y_RANGE_ANDALUCIA.min &&
      feature.y <= Y_RANGE_ANDALUCIA.max
    );
  }

  /**
   * Convierte feature DERA a resultado interno
   */
  private toResult(
    feature: DERAR2Feature,
    confidence: number,
    fuzzyScore?: number
  ): DERAR2Result {
    return {
      x: feature.x,
      y: feature.y,
      confidence,
      precision: DERA_PRECISION_M,
      source: 'dera_r2',
      sourceLevel: 4,
      feature,
      fuzzyScore,
    };
  }

  /**
   * Convierte resultado DERA a GeocodingResult estándar
   */
  private toGeocodingResult(result: DERAR2Result, originalQuery?: string): GeocodingResult {
    return {
      x: result.x,
      y: result.y,
      confidence: result.confidence,
      source: 'dera_r2',
      sourceLayer: result.feature.tipologia,
      originalQuery,
      matchedName: result.feature.nombre,
      matchType: 'fuzzy',
      fuzzyScore: result.fuzzyScore,
      address: result.feature.direccion,
      municipality: result.feature.municipio,
      muniCode: result.feature.cod_ine,
      province: result.feature.provincia,
      metadata: {
        id: result.feature.id,
        tipologia: result.feature.tipologia,
        subtipo: result.feature.subtipo,
        precision: result.precision,
        fuente: result.feature.fuente,
      },
    };
  }

  /**
   * Obtiene tipologías disponibles en DERA
   */
  getAvailableTipologias(): DERATipologia[] {
    return DERA_TIPOLOGIAS;
  }
}

export default DERAR2Geocoder;
